import BaseModal from "~/components/common/base-modal/BaseModal";
import styles from "./LogPaymentModal.module.scss";
import classNames from "classnames/bind";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import { useEffect, useState } from "react";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { Checkbox } from "@mui/material";
import { IInvoiceInLogPaymentModal } from "~/utils/interface/jobDetails";
import jobDetailsApi from "~/apis/jobDetails";
import dayjs from "dayjs";
import { notify } from "~/utils/helper";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";

const cx = classNames.bind(styles);

interface LogPaymentModalProps {
  idJob: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  paymentId?: string | null;
  mode?: "create" | "edit";
  invoiceId?: string;
}

const PAYMENT_METHODS = [
  { label: "None", value: "None" },
  { label: "Cash", value: "Cash" },
  { label: "Credit Card", value: "Credit Card" },
  { label: "Check", value: "Check" },
  { label: "Money Order", value: "Money Order" },
  { label: "Bank Wire", value: "Bank Wire" },
  { label: "Finance", value: "Finance" },
];

interface IInvoice {
  id: string;
  selected: boolean;
  invoiceDate: string;
  invoiceUniqueCode: string;
  originalAmount: string;
  dueDate: string;
  amountDue: string;
  payment: string;
}

interface IFormData {
  paymentDate: string;
  paymentMethod: string;
  reference: string;
  paymentAmount: string;
  applied: number;
  credit: number;
  listInvoice: IInvoice[];
}

const LogPaymentModal = (props: LogPaymentModalProps) => {
  const { idJob, open, onClose, onSuccess, paymentId, mode = "create", invoiceId } = props;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [listInvoice, setListInvoice] = useState<IInvoiceInLogPaymentModal[]>([]);
  const [formData, setFormData] = useState<IFormData>({
    paymentDate: dayjs().format("YYYY-MM-DD"),
    paymentMethod: "",
    reference: "",
    paymentAmount: "",
    applied: 0,
    credit: 0,
    listInvoice: listInvoice?.map((invoice) => ({
      id: invoice.id,
      selected: false,
      invoiceDate: invoice.invoiceDate,
      invoiceUniqueCode: invoice.invoiceUniqueCode,
      originalAmount: invoice.originalAmount,
      dueDate: invoice.dueDate,
      amountDue: invoice.amountDue,
      payment: invoice.amountDue
    })),
  });

  useEffect(() => {
    const fetchListInvoice = async () => {
      try {
        setLoading(true);
        const res = await jobDetailsApi.getListInvoiceInLogPaymentModal(idJob, invoiceId ? { invoiceId } : {});
        setListInvoice(res.data.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    if (open) fetchListInvoice();
    else setErrors({});
  }, [idJob, invoiceId, open]);

  useEffect(() => {
    if (open && mode === "create") {
      setFormData((prev) => ({
        ...prev,
        paymentDate: dayjs().format("YYYY-MM-DD"),
        paymentMethod: "",
        reference: "",
        paymentAmount: "",
        listInvoice: listInvoice?.map((invoice) => ({
          id: invoice.id,
          selected: false,
          invoiceDate: invoice.invoiceDate,
          invoiceUniqueCode: invoice.invoiceUniqueCode,
          originalAmount: invoice.originalAmount,
          dueDate: invoice.dueDate,
          amountDue: invoice.amountDue,
          payment: invoice.amountDue,
        })),
      }));
    }
  }, [listInvoice, open, mode]);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (open && mode === "edit" && paymentId && listInvoice.length > 0) {
        try {
          setLoading(true);
          const res = await jobDetailsApi.getPaymentDetails(idJob, paymentId);
          const paymentData = res.data.data;

          const paymentLogInvoiceIds = paymentData.paymentLogs.map(log => log.invoiceId);

          setFormData((prev) => ({
            ...prev,
            paymentDate: dayjs(paymentData.paymentDate).format("YYYY-MM-DD"),
            paymentMethod: paymentData.paymentMethod,
            reference: paymentData.reference,
            paymentAmount: paymentData.paymentAmount,
            listInvoice: listInvoice.map((invoice) => {
              const paymentLog = paymentData.paymentLogs.find(log => log.invoiceId === invoice.id);
              const isSelected = paymentLogInvoiceIds.includes(invoice.id);
              return {
                id: invoice.id,
                selected: isSelected,
                invoiceDate: invoice.invoiceDate,
                invoiceUniqueCode: invoice.invoiceUniqueCode,
                originalAmount: invoice.originalAmount,
                dueDate: invoice.dueDate,
                amountDue: invoice.amountDue,
                payment: paymentLog ? paymentLog.paymentAmount : invoice.amountDue,
              };
            }),
          }));
        } catch (error) {
          console.log(error);
          notify("error", "Failed to load payment details");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPaymentDetails();
  }, [open, mode, paymentId, idJob, listInvoice]);

  const handleFieldChange = (field: keyof IFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleCheckboxChange = (invoiceId: string) => {
    setFormData((prev) => {
      const updatedListInvoice = prev.listInvoice.map((invoice) => {
        if (invoice.id === invoiceId) {
          const newSelected = !invoice.selected;
          return {
            ...invoice,
            selected: newSelected,
            payment: newSelected ? invoice.amountDue : "0.00",
          };
        }
        return invoice;
      });
      const applied = updatedListInvoice.reduce((acc, invoice) => {
        if (invoice.selected) {
          const payment = parseFloat(invoice.payment) || 0;
          return acc + payment;
        }
        return acc;
      }, 0);
      return {
        ...prev,
        listInvoice: updatedListInvoice,
        paymentAmount: applied.toString(),
      };
    });
  };

  const handlePaymentChange = (invoiceId: string, value: string) => {
    const numValue = parseFloat(value.replace(/[^0-9.]/g, "")) || 0;

    setFormData((prev) => {
      const updatedListInvoice = prev.listInvoice.map((invoice) =>
        invoice.id === invoiceId ? { ...invoice, payment: numValue.toString() } : invoice
      );
      const applied = updatedListInvoice.reduce((acc, invoice) => {
        if (invoice.selected) {
          const payment = parseFloat(invoice.payment) || 0;
          return acc + payment;
        }
        return acc;
      }, 0);
      return {
        ...prev,
        listInvoice: updatedListInvoice,
        paymentAmount: applied.toString(),
      };
    });
  };

  const calculateApplied = () => {
    return formData.listInvoice.reduce((acc, invoice) => {
      if (invoice.selected) {
        const payment = parseFloat(invoice.payment) || 0;
        return acc + payment;
      }
      return acc;
    }, 0);
  };

  // Balance still owed on the selected invoices after this payment (partial payments are allowed).
  const calculateRemaining = () => {
    return formData.listInvoice.reduce((acc, invoice) => {
      if (invoice.selected) {
        return acc + (parseFloat(invoice.amountDue) || 0) - (parseFloat(invoice.payment) || 0);
      }
      return acc;
    }, 0);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.paymentDate.trim()) newErrors.paymentDate = "Payment date is required";
    if (!formData.paymentMethod.trim()) newErrors.paymentMethod = "Payment method is required";
    if (!formData.reference.trim()) newErrors.reference = "Reference is required";

    const selected = formData.listInvoice.filter((invoice) => invoice.selected);
    if (selected.length === 0) {
      newErrors.invoices = "Select at least one invoice to apply the payment to";
    } else if (selected.some((invoice) => !(parseFloat(invoice.payment) > 0))) {
      newErrors.invoices = "Payment for each selected invoice must be greater than $0";
    } else if (
      // edit mode: amountDue already excludes this payment, so the cap would be wrong there
      mode === "create" &&
      selected.some((invoice) => parseFloat(invoice.payment) > parseFloat(invoice.amountDue) + 0.005)
    ) {
      newErrors.invoices = "Payment cannot exceed the invoice's amount due";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSavePayment = async () => {
    if (!validate()) {
      notify("error", "Please fix the highlighted fields before saving");
      return;
    }

    const payload = {
      paymentAmount: calculateApplied(),
      paymentMethod: formData.paymentMethod,
      reference: formData.reference,
      paymentDate: formData.paymentDate,
      status: "Processing",
      paymentLogs: formData.listInvoice
        .filter((invoice) => invoice.selected)
        .map((invoice) => ({
          invoiceId: invoice.id,
          paymentAmount: invoice.payment,
        })),
    }
    try {
      setLoading(true);
      if (mode === "edit" && paymentId) {
        await jobDetailsApi.updatePayment(idJob, paymentId, payload);
        notify("success", "Update payment successfully");
      } else {
        await jobDetailsApi.logPayment(idJob, payload);
        notify("success", "Save payment successfully");
      }
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      notify("error", error.response.data.message || `Failed to ${mode === "edit" ? "update" : "save"} payment`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <BaseModal width="90%" maxWidth="900px" open={open} onClose={onClose}>
      <div className={cx("log-payment-modal-container")}>
        <div className={cx("log-payment-modal-header")}>
          <h3 className={cx("header-title")}>{mode === "edit" ? "Edit Payment" : "Log Payment"}</h3>
        </div>

        <div className={cx("log-payment-modal-body")}>
          {loading ? (
            <div className={cx("loading-container")}>
              <LoadingMainContent />
            </div>
          ) : (
            <>
              <div className={cx("section")}>
                <div className={cx("header-section")}>
                  <h4 className={cx("header-title")}>{formData?.listInvoice[0]?.invoiceUniqueCode} (invoice)</h4>
                </div>

                <div className={cx("row-section", "mb-[10px]")}>
                  <div className={cx("row-item")}>
                    <label className={cx("row-label")}>Payment date<span className="text-red-500">*</span></label>
                    <PrimaryInput
                      name="paymentDate"
                      type="date"
                      placeholder="Select payment date"
                      value={formData.paymentDate}
                      onChange={(e) =>
                        handleFieldChange("paymentDate", e.target.value)
                      }
                      className={cx("input")}
                    />
                    {/* PrimaryInput doesn't render errorMessage, so show it here */}
                    {errors.paymentDate && <p className="text-red-500 text-[12px] mt-[4px]">{errors.paymentDate}</p>}
                  </div>

                  <div className={cx("row-item")}>
                    <label className={cx("row-label")}>Payment method<span className="text-red-500">*</span></label>
                    <PrimarySelectInput
                      name="paymentMethod"
                      placeholder="Select payment method"
                      value={formData.paymentMethod}
                      onChange={(value) =>
                        handleFieldChange("paymentMethod", value)
                      }
                      options={PAYMENT_METHODS}
                      className={cx("select-input")}
                      errorMessage={errors.paymentMethod}
                    />
                  </div>
                </div>

                <div className={cx("row-section")}>
                  <div className={cx("row-item")}>
                    <label className={cx("row-label")}>Reference<span className="text-red-500">*</span></label>
                    <PrimaryInput
                      name="reference"
                      placeholder="Enter reference"
                      value={formData.reference}
                      onChange={(e) =>
                        handleFieldChange("reference", e.target.value)
                      }
                      className={cx("input")}
                    />
                    {errors.reference && <p className="text-red-500 text-[12px] mt-[4px]">{errors.reference}</p>}
                  </div>

                  <div className={cx("row-item")}>
                    <label className={cx("row-label")}>Payment amount</label>
                    <PrimaryInput
                      name="paymentAmount"
                      placeholder="Enter payment amount"
                      value={`$ ${formatCurrency(calculateApplied())}`}
                      disabled
                      className={cx("input")}
                    />
                  </div>
                </div>
              </div>

              <div className={cx("section")}>
                <div className={cx("table-wrapper")}>
                  <table className={cx("invoice-table")}>
                    <thead>
                      <tr className={cx("table-header-row")}>
                        <th className={cx("col-checkbox")}></th>
                        <th className={cx("col-invoice-date")}>Invoice Date</th>
                        <th className={cx("col-invoice-number")}>Invoice #</th>
                        <th className={cx("col-original-amount")}>
                          Original Amount
                        </th>
                        <th className={cx("col-due-date")}>Due Date</th>
                        <th className={cx("col-amount-due")}>Amount Due</th>
                        <th className={cx("col-payment")}>Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.listInvoice.map((invoice) => (
                        <tr key={invoice.id} className={cx("table-data-row")}>
                          <td className={cx("col-checkbox")}>
                            <Checkbox
                              checked={invoice.selected}
                              onChange={() => handleCheckboxChange(invoice.id)}
                              className={cx("checkbox")}
                            />
                          </td>
                          <td className={cx("col-invoice-date")}>
                            {dayjs(invoice.invoiceDate).format("MM/DD/YYYY")}
                          </td>
                          <td className={cx("col-invoice-number")}>
                            {invoice.invoiceUniqueCode}
                          </td>
                          <td className={cx("col-original-amount")}>
                            ${formatCurrency(parseFloat(invoice.originalAmount))}
                          </td>
                          <td className={cx("col-due-date")}>{dayjs(invoice.dueDate).format("MM/DD/YYYY")}</td>
                          <td className={cx("col-amount-due")}>
                            ${formatCurrency(parseFloat(invoice.amountDue))}
                          </td>
                          <td className={cx("col-payment")}>
                            <PrimaryInput
                              name={`payment-${invoice.id}`}
                              value={`$ ${invoice.payment}`}
                              onChange={(e) => {
                                const value = e.target.value;
                                handlePaymentChange(invoice.id, value);
                              }}
                              className={cx("payment-input")}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className={cx("summary-section")}>
                  <div className={cx("summary-item")}>
                    <span className={cx("summary-label")}>Applied:</span>
                    <span className={cx("summary-value")}>
                      ${formatCurrency(calculateApplied())}
                    </span>
                  </div>
                  <div className={cx("summary-item")}>
                    <span className={cx("summary-label")}>Remaining balance:</span>
                    <span className={cx("summary-value")}>
                      ${formatCurrency(calculateRemaining())}
                    </span>
                  </div>
                  {errors.invoices && (
                    <div className={cx("credit-warning")}>{errors.invoices}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={cx("log-payment-modal-footer")}>
          <PrimaryButton
            title="Cancel"
            onClick={onClose}
            className={cx("cancel-button")}
          />
          <PrimaryButton
            title={mode === "edit" ? "Update payment" : "Save payment"}
            onClick={handleSavePayment}
            className={cx("save-payment-button")}
            disabled={loading}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default LogPaymentModal;
